// src/pages/PortalSetPasswordPage.js
// Used for both first-time invite AND password reset (token in ?token=X)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import portalApi from '../utils/portalApi';
import toast from 'react-hot-toast';

export default function PortalSetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [validation, setValidation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidation({ valid: false, error: 'No token provided.' });
      setValidating(false);
      return;
    }
    portalApi.get(`/auth/member/validate-invite-token?token=${encodeURIComponent(token)}`)
      .then(res => setValidation(res.data))
      .catch(() => setValidation({ valid: false, error: 'Failed to validate link.' }))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await portalApi.post('/auth/member/set-password', { token, password });
      setDone(true);
      toast.success('Password set! You can now sign in.');
      setTimeout(() => navigate('/portal/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy, #1a3a5c)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'white', marginBottom: 6 }}>NJJBA</h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Member Portal
          </p>
        </div>
        <div className="card" style={{ padding: '32px 36px' }}>
          {validating ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto' }} />
              <p style={{ marginTop: 16, color: 'var(--gray-500)' }}>Validating link…</p>
            </div>
          ) : !validation?.valid ? (
            <>
              <h2 style={{ marginBottom: 12, color: 'var(--danger)', fontFamily: 'var(--font-serif)' }}>Link Invalid</h2>
              <p style={{ marginBottom: 24, color: 'var(--gray-700)' }}>
                {validation?.error || 'This link is no longer valid.'}
              </p>
              <Link to="/portal/forgot-password" className="btn btn-primary w-full" style={{ justifyContent: 'center' }}>
                Request a New Link
              </Link>
            </>
          ) : done ? (
            <>
              <h2 style={{ marginBottom: 12, color: 'var(--success)', fontFamily: 'var(--font-serif)' }}>✓ Password Set</h2>
              <p style={{ marginBottom: 16, color: 'var(--gray-700)' }}>Redirecting you to sign in…</p>
            </>
          ) : (
            <>
              <h2 style={{ marginBottom: 4, color: 'var(--navy)', fontFamily: 'var(--font-serif)' }}>
                {validation.isReset ? 'Reset Password' : 'Welcome to NJJBA'}
              </h2>
              <p style={{ marginBottom: 20, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                {validation.isReset
                  ? `Choose a new password for ${validation.email}.`
                  : `Hi ${validation.firstName}, set a password to access ${validation.businessName ? `${validation.businessName}'s ` : ''}member portal.`}
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoFocus
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading} style={{ marginTop: 8, justifyContent: 'center' }}>
                  {loading ? 'Setting password…' : (validation.isReset ? 'Reset Password' : 'Set Password & Continue')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
